import { DOCUMENT } from '@angular/common';
import {
    AfterViewInit,
    Directive,
    ElementRef,
    EventEmitter,
    Inject,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    Renderer2,
    SimpleChanges,
    inject
} from '@angular/core';
import { NavigationEnd, NavigationStart } from '@angular/router';
import { App } from '@df/core/app';
import { WINDOW } from '@df/dom/globals';
import { TimeoutService } from '@df/ng/timeout.service';
import { BlockStickyService } from '@df/ui/block-sticky.service';
import { BodyClassService } from '@df/ui/body-class.service';
import { Observable, Subject, Subscription, fromEvent } from 'rxjs';
import { flatMap, map, takeUntil } from 'rxjs/operators';

export class StickyNativeEvent {
    constructor(
        public stuck: boolean,
        public target: HTMLElement
    ) {}
}

export enum StickyNativeDirectionEnum {
    UP = 'up',
    DOWN = 'down'
}

export enum StickyNativeSentinelPositionEnum {
    HEADER = 'header',
    FOOTER = 'bottom'
}

@Directive({
    selector: '[stickyNative]',
    standalone: true
})
export class StickyNativeDirective implements AfterViewInit, OnChanges, OnDestroy {
    /**
     * It must be a top container of the sticky element and of the element
     * that will trigger the custom class on the sticky element.
     * This should be the element with the scroll
     * If an string is provided, it must be the ID of the element.
     */
    @Input() set stickyNativeScrollContainer(value: string | ElementRef | HTMLElement | Window) {
        this._setHTMLElement('_scrollContainer', value);
    }
    get stickyNativeScrollContainer(): string | ElementRef | HTMLElement | Window {
        return this._scrollContainer || this.app.client.getRootElement();
    }
    private _scrollContainer: HTMLElement;

    /**
     * When the sticky element bypass this element the custom class will apply.
     * If an string is provided, it must be the ID of the element.
     */
    @Input() set stickyNativeTriggerOn(value: string | ElementRef | HTMLElement) {
        this._setHTMLElement('_triggerOn', value);
    }
    get stickyNativeTriggerOn(): string | ElementRef | HTMLElement {
        return this._triggerOn || this.elementRef.nativeElement.parentElement;
    }
    private _triggerOn: HTMLElement;

    /**
     * zIndex value to set to the target element
     */
    @Input() stickyNativeZIndex = 10;

    readonly window = inject(WINDOW);

    /**
     * Top value to set to the target element
     */
    @Input() stickyNativeTop = 0;

    /**
     * Class added to the page body
     */
    @Input() stickyNativeBodyClass?: string;

    @Input() stickyNativeDetectDirection: boolean;

    @Input() stickyNativeOff: boolean;

    @Input() stickyNativeHeaderOnly: boolean;

    @Output() stickyNativeChange = new EventEmitter<StickyNativeEvent>();
    @Output() stickyNativeDetectDirectionChange = new EventEmitter<StickyNativeDirectionEnum>();

    /** @internal */
    private readonly destroyed$ = new Subject<void>();
    private _scrollSubscription: Subscription;
    private _lastScrollTop = 0;
    private _blockDetection = false;
    private _detectTimeout;
    private _detectDirectionThrottled = this.timeoutService.throttle(() => this._detectDirection(), 50);
    private _fireEventDebounced = this.timeoutService.debounce((stuck = false) => this._fireEvent(stuck), 100);

    private initiated = false;
    private isSticky = false;
    private listenersSet = false;

    private cleanups: (() => void)[] = [];

    constructor(
        private elementRef: ElementRef<HTMLElement>,
        @Inject(DOCUMENT) private documentRef: Document,
        private app: App,
        private renderer2: Renderer2,
        private timeoutService: TimeoutService,
        private bodyClassService: BodyClassService,
        private blockStickyService: BlockStickyService
    ) {}

    ngAfterViewInit(): void {
        if (!this.app.isPlatformBrowser) {
            return;
        }
        this.timeoutService.async(() => {
            this.blockStickyService.state$.pipe(takeUntil(this.destroyed$)).subscribe(() => this.applySticky());
            this.applyListeners();
            this.initiated = true;
            this.applySticky();
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.app.isPlatformBrowser) {
            return;
        }

        if (changes['stickyNativeOff']) {
            this.applySticky();
        }
    }

    ngOnDestroy(): void {
        this._reset();

        this.destroyed$.next();
        this.destroyed$.complete();
    }

    private applyListeners(): void {
        // NOTE: we should also be cleaning up when switchin off, but I dont want to make more changes
        if (!this.isStickyOff()) {
            if (!this.listenersSet) {
                this.cleanups.push(
                    this.renderer2.listen(this.documentRef.body, 'click', () => {
                        this._blockDetection = true;

                        clearTimeout(this._detectTimeout);

                        this._detectTimeout = this.timeoutService.setTimeout(() => {
                            this._blockDetection = false;
                        }, 100);
                    })
                );

                this.app.meta.routerEvents.pipe(takeUntil(this.destroyed$)).subscribe(event => {
                    if (event instanceof NavigationStart) {
                        this._reset();
                    } else if (event instanceof NavigationEnd) {
                        this.applySticky();
                    }
                });

                this._observeIntersection();
                this._warnings();

                this.listenersSet = true;
            }
        }
    }

    private applySticky(): void {
        if (!this.initiated) {
            return;
        }
        if (this.isStickyOff()) {
            this._reset();
        } else {
            this._makeSticky();
        }

        this._detectDirection();
    }

    private isStickyOff() {
        return this.stickyNativeOff || this.blockStickyService.state$.value;
    }

    /**
     * Add/Remove class to the target element when the sentinel element disappear/appear
     */
    private _fireEvent(stuck = false): void {
        if (this.stickyNativeOff) {
            return;
        }

        this._toggleClass(stuck);

        this.stickyNativeChange.emit(new StickyNativeEvent(stuck, this.elementRef.nativeElement));
    }

    /**
     * Make the main element sticky.
     * Put the necessary css to make it happen.
     */
    private _makeSticky(): void {
        if (this.isSticky) {
            return;
        }
        const nativeElement: HTMLElement = this.elementRef.nativeElement;

        this.renderer2.addClass(nativeElement, 'sticky-native');
        this.renderer2.setStyle(nativeElement, 'position', '-webkit-sticky');
        this.renderer2.setStyle(nativeElement, 'position', 'sticky');
        this.renderer2.setStyle(nativeElement, 'top', `var(--sticky-native-top, ${this.stickyNativeTop}px)`);
        this.renderer2.setStyle(nativeElement, 'zIndex', `var(--sticky-native-z-index, ${this.stickyNativeZIndex})`);

        this._scrollSubscription?.unsubscribe();

        this._scrollSubscription = fromEvent(this.app.client.scrollElementForEvents as HTMLElement, 'scroll')
            .pipe(takeUntil(this.destroyed$))
            .subscribe(() => this._detectDirectionThrottled());
    }

    /**
     * Notifies when elements w/ the `sticky` class begin to stick or stop sticking.
     * Note: the elements should be children of `container`.
     */
    private _observeIntersection(): void {
        this.timeoutService.ngZone.runOutsideAngular(() => {
            this._addSentinel(StickyNativeSentinelPositionEnum.HEADER);

            if (!this.stickyNativeHeaderOnly) {
                this._addSentinel(StickyNativeSentinelPositionEnum.FOOTER);
            }
        });
    }

    private _createHeaderObserver(sentinel: HTMLElement): Observable<unknown> {
        return new Observable(observer => {
            const intersectionObserver = new IntersectionObserver(entries => {
                observer.next(entries);
            });

            intersectionObserver.observe(sentinel);

            return () => {
                intersectionObserver.disconnect();
            };
        }).pipe(
            flatMap((entries: IntersectionObserverEntry[]) => entries),
            map(entry => {
                if (this.stickyNativeOff || entry === null) {
                    return;
                }

                const { boundingClientRect, isIntersecting, rootBounds } = entry;
                const stickyParent = entry.target.parentElement;
                const bottom = boundingClientRect.bottom;
                const rootBoundsBottom = rootBounds?.bottom || 0;
                const rootBoundsTop = rootBounds?.top || 0;

                stickyParent.style.position = 'relative';

                if (bottom < rootBoundsBottom && isIntersecting) {
                    this._fireEvent(false);
                } else if (bottom < rootBoundsTop && !isIntersecting) {
                    this._fireEvent(true);
                }
            })
        );
    }

    private _createObserver(): Observable<unknown> {
        return new Observable(observer => {
            const intersectionObserver = new IntersectionObserver(
                entries => {
                    observer.next(entries);
                },
                {
                    threshold: [0, 1],
                    rootMargin: '-1px 0px -1px 0px',
                    root: this.stickyNativeTriggerOn as HTMLElement
                }
            );

            intersectionObserver.observe(this.elementRef.nativeElement);

            return () => {
                intersectionObserver.disconnect();
            };
        }).pipe(
            flatMap((entries: IntersectionObserverEntry[]) => entries),
            map(entry => {
                if (this.stickyNativeOff) {
                    return;
                }

                this._fireEventDebounced(entry.intersectionRatio === 1);
            })
        );
    }

    private _detectDirection(): void {
        if (this._blockDetection || !this.initiated) {
            return;
        }

        if (!this.isSticky) {
            const scrollTop: number =
                (<HTMLElement>this.stickyNativeScrollContainer).scrollTop || (<Window>this.stickyNativeScrollContainer).scrollY;
            let _direction: StickyNativeDirectionEnum;

            if (this.stickyNativeDetectDirection) {
                if (scrollTop > this._lastScrollTop) {
                    this._toggleUp();
                    this._toggleDown(true);

                    _direction = StickyNativeDirectionEnum.DOWN;
                } else {
                    this._toggleDown();

                    _direction = undefined;

                    if (this._lastScrollTop > scrollTop) {
                        this._toggleUp(true);

                        _direction = StickyNativeDirectionEnum.UP;
                    }
                }
            }

            this._lastScrollTop = scrollTop <= 0 ? 0 : scrollTop;

            this.stickyNativeDetectDirectionChange.emit(_direction);
        } else {
            this._lastScrollTop = 0;
            this._toggleClass();
            this._toggleUp();
            this._toggleDown();
        }
    }

    private _toggleClass(to = false): void {
        const className = 'is-sticky';
        to
            ? this.renderer2.addClass(this.elementRef.nativeElement, className)
            : this.renderer2.removeClass(this.elementRef.nativeElement, className);

        if (this.stickyNativeBodyClass) {
            this.bodyClassService.set(`${this.stickyNativeBodyClass}-${className}`, this, to);
        }
    }

    private _toggleUp(to = false): void {
        const className = 'is-sticky-up';
        to
            ? this.renderer2.addClass(this.elementRef.nativeElement, className)
            : this.renderer2.removeClass(this.elementRef.nativeElement, className);

        if (this.stickyNativeBodyClass) {
            this.bodyClassService.set(`${this.stickyNativeBodyClass}-${className}`, this, to);
        }
    }

    private _toggleDown(to = false): void {
        const className = 'is-sticky-down';
        to
            ? this.renderer2.addClass(this.elementRef.nativeElement, className)
            : this.renderer2.removeClass(this.elementRef.nativeElement, className);

        if (this.stickyNativeBodyClass) {
            this.bodyClassService.set(`${this.stickyNativeBodyClass}-${className}`, this, to);
        }
    }

    /**
     * Transform if needed into HTMLElement a value given to be set in a property
     */
    private _setHTMLElement(prop: string, value: string | ElementRef | HTMLElement | Window): void {
        if (typeof value === 'string') {
            this[prop] = this.documentRef.querySelector(value);
        } else if (value instanceof ElementRef) {
            this[prop] = value.nativeElement;
        } else {
            this[prop] = value;
        }
    }

    private _addSentinel(position: StickyNativeSentinelPositionEnum): void {
        if (position === StickyNativeSentinelPositionEnum.HEADER) {
            const sentinel = this._generateSentinelElement(position);
            this._createHeaderObserver(sentinel).pipe(takeUntil(this.destroyed$)).subscribe();
        } else {
            this._createObserver().pipe(takeUntil(this.destroyed$)).subscribe();
        }
    }

    /**
     * Generates the sentinel element with the necessary styles
     */
    private _generateSentinelElement(position: StickyNativeSentinelPositionEnum): HTMLElement {
        const sentinelElement = this.renderer2.createElement('div');
        const elementRect = this.elementRef.nativeElement.getBoundingClientRect();
        const targetOnRect = (<HTMLElement>this.stickyNativeTriggerOn).getBoundingClientRect();

        this.renderer2.addClass(sentinelElement, `sticky-native__sentinel-${position}`);
        this.renderer2.setStyle(sentinelElement, 'right', '0');
        this.renderer2.setStyle(sentinelElement, 'left', '0');
        this.renderer2.setStyle(sentinelElement, 'zIndex', '99');
        this.renderer2.setStyle(sentinelElement, 'width', '100%');
        // this.renderer.setStyle(sentinelElement, 'backgroundColor', 'rgba(255, 0, 0, 0.05)');
        this.renderer2.setStyle(sentinelElement, 'pointerEvents', 'none');

        if (position === StickyNativeSentinelPositionEnum.HEADER) {
            const top = elementRect.top - targetOnRect.top;

            this.renderer2.setStyle(
                sentinelElement,
                'top',
                `calc(var(--sticky-native-sentinel-top-top, ${top}px) + var(--sticky-native-sentinel-top-offset, 0))`
            );

            if (!this.stickyNativeHeaderOnly) {
                this.renderer2.setStyle(sentinelElement, 'position', 'absolute');
            }

            this.renderer2.setStyle(sentinelElement, 'height', '0px');

            this.elementRef.nativeElement.parentElement.insertBefore(sentinelElement, this.elementRef.nativeElement);
        } else {
            const window = this.window;
            const parentStyle = (window as Window).getComputedStyle(this.elementRef.nativeElement.parentElement);
            const parentPadding = parseInt(parentStyle.paddingTop, 10);
            const { height } = this.elementRef.nativeElement.getBoundingClientRect();

            this.renderer2.setStyle(sentinelElement, 'position', 'absolute');
            this.renderer2.setStyle(sentinelElement, 'bottom', 0);
            this.renderer2.setStyle(sentinelElement, 'height', `var(--sticky-native-sentinel-bottom-height, ${height + parentPadding}px)`);

            this.elementRef.nativeElement.parentElement.appendChild(sentinelElement);
        }

        return sentinelElement;
    }

    private _reset(): void {
        if (!this.isSticky) {
            return;
        }
        this._scrollSubscription?.unsubscribe();

        this._lastScrollTop = 0;
        this._blockDetection = false;
        this._toggleClass();
        this._toggleDown();
        this._toggleUp();
    }

    /**
     * Show warnings about `position: sticky` required options.
     */
    private _warnings(): void {
        const triggerOn = this.stickyNativeTriggerOn as HTMLElement;

        if (triggerOn.getBoundingClientRect().height <= this.elementRef.nativeElement.getBoundingClientRect().height) {
            this.app.logger.warn(
                // tslint:disable-next-line
                "[stickyNative] - If the parent element has no height set then the sticky element won't have any area " +
                    'to stick to when scrolling. This happens because the sticky element is meant to stick/scroll ' +
                    'within the height of a container.'
            );
        }
    }
}
